import React, { FC, useEffect, useState } from 'react'
import t from 'src/translations/main'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'
import useForm from 'src/components/Core/Hooks/useForm/useForm'
import utils from 'src/components/Organisms/Form/utils'
import config from './config'
import updateModal from 'src/components/Sections/Modal/updateModal'
import storage from 'src/components/Core/Utils/storage'
import { parseJson } from 'src/views/Pages/Onboarding/FormWrapper'
import { useDropdownOptions, useShareHolderDetails, useShareHolderDetailsUpdate } from 'src/components/Core/Context/OnboardingContext'

import Button from 'src/components/Atoms/Button/Button'
import Spinner from 'src/components/Core/Loading/Spinner/Spinner'
import Popup from 'src/components/Atoms/Popup/Popup'

import {
  FormStyle,
  FormFooter,
  FormFooterActionsRight,
} from 'src/components/Organisms/Form/Form.styles'

import { ApiData, TableObj } from 'src/views/Pages/Onboarding/types'
import { ModalOwnershipDetailsProps } from './types'
import { getBaseApiHost } from 'src/components/Core/Utils/url_constants'

const ModalOwnershipDetailsForm: FC<ModalOwnershipDetailsProps> = props => {
  const { actionType } = props
  const [serverValidationError, setServerValidationError] = useState('')
  const [documentInfo, setDocumentInfo] = useState()
  const [documentIdUpload, setDocumentIdUpload] = useState(0)
  const { isLoading, error, sendRequest } = useFetch()
  const tableData = useShareHolderDetails()
  const updateShareHoldersTableData = useShareHolderDetailsUpdate()
  const { dropdownOptions } = useDropdownOptions()

  const getFetchedData = () => {
    const rowData = [...tableData].filter(
      tableDataObj => tableDataObj.id === props.id
    )
    const ubos = {
      ...utils.transformTableObj(
        {
          ...rowData[0],
          col11: (dropdownOptions?.countries?.find(country => country.label === rowData[0].col11))?.index?.toString()
        },
        props.tableConfig
      ),
      cscDocument: rowData[0].col13
    }
    return {
      ...ubos,
      // @ts-ignore
      isUBOOwnershipDirect: ubos.isUBOOwnershipDirect === 'Yes' ? 'true' : 'false',
      shareholderResidentialAddress: rowData[0].col3.split(`, ${rowData[0].col10}`)[0]
    }
  }

  const fetchedData = actionType === 'add' ? {} : getFetchedData()

  const submitForm = (submitData: ApiData) => {
    // @ts-ignore
    if (actionType === 'edit') submitData.data.id = fetchedData.id
    submitData.data.applicationId = storage?.get(storage?.keys?.onboarding?.applicationId)
    // eslint-disable-next-line
    const modifyResponse = (data: any) => {
      const serverValidationErrorsParsed = parseJson(data?.reasonPhrase)
      if(serverValidationErrorsParsed && serverValidationErrorsParsed[0].key === 'generalError') {
        setServerValidationError(t(`onboarding.errors.${serverValidationErrorsParsed[0].errorcode}`) || serverValidationErrorsParsed[0].errordescription?.split(':')[1] || t('error.invalidValue'))
        return
      }
      if(Array.isArray(serverValidationErrorsParsed) && serverValidationErrorsParsed.length > 0) {
        submitData.setConfigData(utils.injectErrorPropToConfig(submitData.configData, [], serverValidationErrorsParsed))
        return
      }
      if (data) {
        updateModal({ modalState: 'close' })
        if (actionType === 'add') {
          submitData.data.id = data
          updateShareHoldersTableData((prevState: TableObj[]) => {
            const country = dropdownOptions.countries?.find(country => country?.index.toString() === submitData?.data?.shareholderCountry)?.label
            return utils.addRowToTableData(
              [...prevState],
              {
                ...submitData.data,
                shareholderCountry: country,
                shareholderResidentialAddress:`${submitData?.data?.shareholderResidentialAddress}, ${submitData?.data?.shareholderPostcode}, ${submitData?.data?.shareholderTown}, ${country?.replace?.(country?.substring?.(0,1),country?.substring?.(0,1).toUpperCase?.()).replace?.(country?.substring?.(1),country?.substring?.(1).toLowerCase?.())}`,
                isUBOOwnershipDirect: submitData.data.isUBOOwnershipDirect === 'true' ? 'Yes' : 'No',
                cscDocument: submitData.data.isUBOOwnershipDirect === 'true'
                  ? {
                    documentFileExtension: null,
                    documentFileType: null,
                    documentID: 0,
                    documentName: null,
                    documentSection: null,
                    documentType: null,
                  }
                  : documentInfo
              },
              props.tableConfig,
              props.action
            )
          })
        }
        if (actionType === 'edit') {
          updateShareHoldersTableData((prevState: TableObj[]) => {
            const country = dropdownOptions.countries?.find(country => country?.index.toString() === submitData?.data?.shareholderCountry)?.label
            return utils.getEditedTableData(
              [...prevState],
              {
                ...submitData.data,
                shareholderCountry: country,
                shareholderResidentialAddress:`${submitData?.data?.shareholderResidentialAddress}, ${submitData?.data?.shareholderPostcode}, ${submitData?.data?.shareholderTown}, ${country?.replace?.(country?.substring?.(0,1),country?.substring?.(0,1).toUpperCase?.()).replace?.(country?.substring?.(1),country?.substring?.(1).toLowerCase?.())}`,
                isUBOOwnershipDirect: submitData.data.isUBOOwnershipDirect === 'true' ? 'Yes' : 'No',
                cscDocument: submitData.data.isUBOOwnershipDirect === 'true'
                  ? {
                    documentFileExtension: null,
                    documentFileType: null,
                    documentID: 0,
                    documentName: null,
                    documentSection: null,
                    documentType: null,
                  }
                  : documentInfo
              },
              props.tableConfig,
              props.action
            )
          })
        }
      }
    }

    if(submitData.data.ownershipPercentage === 0) {
      submitData.setConfigData(utils.injectErrorPropToConfig(submitData.configData, [], [{key: 'ownershipPercentage', errorcode: 'ownershipPercentageZero'}]))
      return
    }

    if(submitData.data.ownershipPercentage < 25) {
      submitData.setConfigData(utils.injectErrorPropToConfig(submitData.configData, [], [{key: 'ownershipPercentage', errorcode: 'ownershipPercentageSmallerThan25'}]))
      return
    }

    let sumOfPercentages = 0
    if (actionType === 'edit') {
      sumOfPercentages = tableData.filter(item => item.id !== props.id).reduce((acc, curr) => {
        acc = acc + curr.col4
        return acc
      }, 0)
    } else {
      sumOfPercentages = tableData.reduce((acc, curr) => {
        acc = acc + curr.col4
        return acc
      }, 0)
    }
    sumOfPercentages += submitData.data.ownershipPercentage
    if(sumOfPercentages <= 100) {
      // @ts-ignore
      if(submitData.data.isUBOOwnershipDirect === 'true' && documentInfo?.documentID !== 0){
        sendRequest(
          {
            scope: 'onboarding',
            url: import.meta.env.VITE_MOCK_DATA !== 'true'
              // @ts-ignore
              ? `${getBaseApiHost()}/CorpAOF/RemoveDocument?applicationId=${storage?.get(storage?.keys?.onboarding?.applicationId)}&DocumentID=${documentInfo.documentID}`
              : `${window.location.origin}/mock/deleteFile.json`,
            method: 'DELETE',
          },
          () => {
            setDocumentIdUpload(0)
            setDocumentInfo({
              // @ts-ignore
              documentFileExtension: null,
              documentFileType: null,
              documentID: 0,
              documentName: null,
              documentSection: null,
              documentType: null,
            })
          },
        )

      }
      sendRequest(
        {
          scope: 'onboarding',
          url: props.postUrl,
          method: 'POST',
          body: formData?.isUBOOwnershipDirect?.value === 'true' ? submitData.data : {...submitData.data,  DocumentId: documentIdUpload.toString()},
        },
        modifyResponse
      )
    } else {
      submitData.setConfigData(utils.injectErrorPropToConfig(submitData.configData, [], [{key: 'ownershipPercentage', errorcode: 'ownershipPercentage'}]))
    }

  }

  const onFileUpload = (file: ApiData, id: number, data: number) => {
    setDocumentIdUpload(data)
    setDocumentInfo({
      // @ts-ignore
      documentFileExtension: file.data.type,
      documentFileType: '',
      documentID: data,
      documentName: file.name,
      documentSection: '5',
      documentType: 'cscDocument',
    })
  }
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const onFileDelete = (data: Record<string, unknown>) => {
  /* eslint-enable @typescript-eslint/no-unused-vars */
    setDocumentIdUpload(0)
    setDocumentInfo({
      // @ts-ignore
      documentFileExtension: null,
      documentFileType: null,
      documentID: 0,
      documentName: null,
      documentSection: null,
      documentType: null,
    })
    const rowDataIndex = [...tableData].findIndex(
      tableDataObj => tableDataObj.id === props.id
    )

    if(actionType === 'edit'){
      // @ts-ignore
      updateShareHoldersTableData(prev => {
        const temp = [...prev]
        temp[`${rowDataIndex}`].col13 = {
          documentFileExtension: null,
          documentFileType: null,
          documentID: 0,
          documentName: null,
          documentSection: null,
          documentType: null,
        }
        return temp
      })
    }

  }

  const {
    formData,
    emptyValidation,
    handleInputChange,
    handleSubmit,
    toggleShowByName,
    toggleShowByVisualName,
    configData
  } = useForm(
    fetchedData,
    config({ countries: dropdownOptions.countries }, onFileUpload, onFileDelete),
    submitForm
  )

  useEffect(() => {

    toggleShowByName(
      'entityStateName',
      formData?.isUBOOwnershipDirect?.value === 'false'
    )

    toggleShowByVisualName('chartNoteText', formData?.isUBOOwnershipDirect?.value === 'false' ? 'true' : 'false')
    toggleShowByVisualName('chartFile', formData?.isUBOOwnershipDirect?.value === 'false' ? 'true' : 'false')

  }, [formData?.isUBOOwnershipDirect?.value])

  useEffect(() => {
    if(tableData?.length > 0) {
      const rowData = [...tableData].filter(
        tableDataObj => tableDataObj.id === props.id
      )
      setDocumentInfo(rowData[0]?.col13 || {
        documentFileExtension: null,
        documentFileType: null,
        documentID: 0,
        documentName: null,
        documentSection: null,
        documentType: null,
      })
      if(rowData[0]?.col13.documentID !== 0) {
        setDocumentIdUpload(rowData[0]?.col13.documentID)
      }
      return
    }
    setDocumentInfo({
      // @ts-ignore
      documentFileExtension: null,
      documentFileType: null,
      documentID: 0,
      documentName: null,
      documentSection: null,
      documentType: null,
    })
  },[])

  if (isLoading) return <Spinner removeFullscreen />

  if (error) return <p>{error?.message || error}</p>

  return (
    <FormStyle onSubmit={handleSubmit}>
      {utils.sectionBuilder(configData, formData, handleInputChange)}
      <FormFooter insideModal>
        <FormFooterActionsRight>
          <Button
            version={2}
            label={t('onboarding.main.cancelButton')}
            onClick={() => {
              // @ts-ignore
              if(documentInfo && documentInfo.documentID !== 0 && actionType === 'add'){
                sendRequest(
                  {
                    scope: 'onboarding',
                    url: import.meta.env.VITE_MOCK_DATA !== 'true'
                      // @ts-ignore
                      ? `${getBaseApiHost()}/CorpAOF/RemoveDocument?applicationId=${storage?.get(storage?.keys?.onboarding?.applicationId)}&DocumentID=${documentInfo.documentID}`
                      : `${window.location.origin}/mock/deleteFile.json`,
                    method: 'DELETE',
                  },
                  () => updateModal({ modalState: 'close' }),
                  () => updateModal({ modalState: 'close' })
                )
              }
              updateModal({ modalState: 'close' })

            }}
          />
          <Button
            type='submit'
            label={t('onboarding.main.saveButton')}
            disabled={emptyValidation}
          />
        </FormFooterActionsRight>
      </FormFooter>
      {!isLoading && (error || serverValidationError) && <Popup type='error' label={error || serverValidationError} popupContainerWidth='auto' popupContainerHeight='auto'/>}
    </FormStyle>
  )
}

export default ModalOwnershipDetailsForm
